import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { cn } from '@/lib/cn'

/**
 * Every form control in the app.
 *
 * The label, the hint and the error are wired together here — `htmlFor`, `aria-describedby`
 * and `aria-invalid` — because doing it per-form is how half the fields end up unlabelled.
 */

interface FieldShellProps {
  label: string
  hint?: string
  error?: string | undefined
  isRequired?: boolean
  className?: string
  children: (ids: { inputId: string; describedBy: string | undefined; isInvalid: boolean }) => ReactNode
}

export function Field({
  label,
  hint,
  error,
  isRequired = false,
  className,
  children,
}: FieldShellProps) {
  const inputId = useId()
  const hintId = `${inputId}-hint`
  const errorId = `${inputId}-error`

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ')

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={inputId} className="text-[0.8125rem] leading-5 font-medium text-text">
        {label}
        {isRequired ? (
          <span className="ml-0.5 text-danger" aria-hidden>
            *
          </span>
        ) : null}
      </label>

      {children({ inputId, describedBy: describedBy || undefined, isInvalid: Boolean(error) })}

      {hint && !error ? (
        <p id={hintId} className="text-xs leading-4 text-text-secondary">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="text-xs leading-4 text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}

const CONTROL = [
  'w-full rounded-control border bg-surface px-3 text-sm text-text',
  'transition-colors placeholder:text-text-muted',
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-muted',
  'aria-[invalid=true]:border-danger',
].join(' ')

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className'> {
  label: string
  hint?: string
  error?: string | undefined
  className?: string
}

export function TextField({ label, hint, error, className, ...props }: TextFieldProps) {
  return (
    <Field
      label={label}
      {...(hint ? { hint } : {})}
      error={error}
      isRequired={props.required ?? false}
      {...(className ? { className } : {})}
    >
      {({ inputId, describedBy, isInvalid }) => (
        <input
          id={inputId}
          aria-describedby={describedBy}
          aria-invalid={isInvalid || undefined}
          className={cn(CONTROL, 'h-9 border-border-strong/60')}
          {...props}
        />
      )}
    </Field>
  )
}

export interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'className'> {
  label: string
  hint?: string
  error?: string | undefined
  className?: string
}

export function TextAreaField({ label, hint, error, className, ...props }: TextAreaFieldProps) {
  return (
    <Field
      label={label}
      {...(hint ? { hint } : {})}
      error={error}
      isRequired={props.required ?? false}
      {...(className ? { className } : {})}
    >
      {({ inputId, describedBy, isInvalid }) => (
        <textarea
          id={inputId}
          aria-describedby={describedBy}
          aria-invalid={isInvalid || undefined}
          className={cn(CONTROL, 'min-h-20 resize-y border-border-strong/60 py-2')}
          {...props}
        />
      )}
    </Field>
  )
}

export interface SelectOption {
  readonly value: string
  readonly label: string
}

export interface SelectFieldProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'className' | 'children'> {
  label: string
  options: readonly SelectOption[]
  hint?: string
  error?: string | undefined
  className?: string
}

export function SelectField({
  label,
  options,
  hint,
  error,
  className,
  ...props
}: SelectFieldProps) {
  return (
    <Field
      label={label}
      {...(hint ? { hint } : {})}
      error={error}
      isRequired={props.required ?? false}
      {...(className ? { className } : {})}
    >
      {({ inputId, describedBy, isInvalid }) => (
        <select
          id={inputId}
          aria-describedby={describedBy}
          aria-invalid={isInvalid || undefined}
          className={cn(CONTROL, 'h-9 cursor-pointer border-border-strong/60 pr-8')}
          {...props}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  )
}

export interface SwitchProps {
  label: string
  description?: string
  isChecked: boolean
  onChange: (next: boolean) => void
  isDisabled?: boolean
  /** Explains why it is disabled — a control that cannot move should say so. */
  disabledReason?: string | undefined
}

/**
 * A real checkbox under the paint.
 *
 * A `div` with `role="switch"` has to reimplement keyboard activation, form participation and
 * the browser's own focus behaviour; an input does all three already.
 */
export function Switch({
  label,
  description,
  isChecked,
  onChange,
  isDisabled = false,
  disabledReason,
}: SwitchProps) {
  const id = useId()
  const descriptionId = `${id}-description`
  const text = isDisabled && disabledReason ? disabledReason : description

  return (
    <div className="flex items-start gap-3">
      <span className="relative mt-0.5 inline-flex shrink-0">
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={isChecked}
          disabled={isDisabled}
          aria-describedby={text ? descriptionId : undefined}
          onChange={(event) => {
            onChange(event.target.checked)
          }}
          className="peer size-0 appearance-none opacity-0"
        />
        <label
          htmlFor={id}
          aria-hidden
          className={cn(
            'block h-5 w-9 rounded-full border transition-colors',
            'peer-focus-visible:outline peer-focus-visible:outline-2',
            'peer-focus-visible:outline-offset-2 peer-focus-visible:outline-focus',
            isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            isChecked ? 'border-primary bg-primary' : 'border-border-strong bg-surface-sunken',
          )}
        >
          <span
            className={cn(
              'mt-0.5 block size-3.5 rounded-full bg-surface-raised shadow-sm transition-transform',
              isChecked ? 'translate-x-[1.125rem]' : 'translate-x-0.5',
            )}
          />
        </label>
      </span>

      <div className="min-w-0">
        <label
          htmlFor={id}
          className={cn(
            'block text-[0.8125rem] leading-5 font-medium text-text',
            isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          {label}
        </label>
        {text ? (
          <p id={descriptionId} className="mt-0.5 text-xs leading-4 text-text-secondary">
            {text}
          </p>
        ) : null}
      </div>
    </div>
  )
}

export interface CheckboxProps {
  label: ReactNode
  isChecked: boolean
  onChange: (next: boolean) => void
  isDisabled?: boolean
  description?: ReactNode
}

export function Checkbox({
  label,
  isChecked,
  onChange,
  isDisabled = false,
  description,
}: CheckboxProps) {
  const id = useId()

  return (
    <div className="flex items-start gap-2.5">
      <input
        id={id}
        type="checkbox"
        checked={isChecked}
        disabled={isDisabled}
        onChange={(event) => {
          onChange(event.target.checked)
        }}
        className={cn(
          'mt-0.5 size-4 shrink-0 cursor-pointer rounded-[0.25rem]',
          'border border-border-strong accent-primary',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
      />
      <div className="min-w-0">
        <label
          htmlFor={id}
          className={cn(
            'block text-[0.8125rem] leading-5 text-text',
            isDisabled ? 'cursor-not-allowed text-text-muted' : 'cursor-pointer',
          )}
        >
          {label}
        </label>
        {description ? (
          <p className="mt-0.5 text-xs leading-4 text-text-secondary">{description}</p>
        ) : null}
      </div>
    </div>
  )
}
